import { Col, Row } from "antd";
import classNames from "classnames";
import { useFormik } from "formik";
import { debounce, get, isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdClose } from "react-icons/md";
import { useMediaQuery } from "react-responsive";
import { useLazyQuery } from "shared/hooks/useApi";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CAPENCY_ADDRESS,
  GOV_ADDRESS_FRANCE,
} from "utils/api/graphql/queries/referentials";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import { bindInputProps, bindWebTextFieldInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import AutoCompleteInput from "../Components/AutoComplete";
import Head from "../Components/Head";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import InputChecked from "../Components/svg/check_small.svg";
import InputError from "../Components/svg/error.svg";
import validation from "./validation";

const View = ({ onNext, initialValues, locked, onBack }) => {
  const [isAddressValid, setIsAddressValid] = useState(false);

  const [showManualForm, setShowManualForm] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [selectedAddressLabel, setSelectedAddressLabel] = useState("");
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.ADHERENT_ADDRESS", { returnObjects: true });
  const [govAddressAutoComplete] = useLazyQuery(GOV_ADDRESS_FRANCE);
  const [capencyAddressAutoComplete] = useLazyQuery(CAPENCY_ADDRESS);

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  const { cities, loading: citiesLoading } = useZipCodeCities(
    get(values, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`),
  );

  const addressValues = (source) => ({
    street: get(source, `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`),
    streetNumber: get(
      source,
      `telecoms.${CONTACT_POINT_SYSTEM.ADDRESS_LOCATION}.value`,
    ),
    zipCode: get(source, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`),
    city: get(source, `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`),
  });

  const searchLineCapency = debounce((inputValue) => {
    const zipCode = get(
      initialValues,
      `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
    );
    capencyAddressAutoComplete({
      variables: {
        where: {
          input: inputValue,
          zipCode,
        },
      },
      onCompleted: ({ capencyAddressAutoComplete }) => {
        const suggestions = (capencyAddressAutoComplete || []).map((item) => ({
          label: `${item.streetNumber || ""} ${item.street || ""}, ${
            item.postalCode || ""
          } ${item.city || ""}`.trim(),
          value: `${item.streetNumber || ""} ${item.street || ""} ${
            item.postalCode || ""
          } ${item.city || ""}`.trim(),
          postalCode: item.postalCode,
          city: item.city,
          street: item.street,
          streetNumber: item.streetNumber,
        }));

        const uniqueSuggestions = Array.from(
          new Map(suggestions.map((item) => [item.value, item])).values(),
        );

        setAutocompleteOptions(uniqueSuggestions);
      },
    });
  }, 1000);

  const defaultValue = useMemo(() => {
    const { street, zipCode, city } = addressValues(values);
    if (!street) return null;
    const label = `${street || ""} ${street ? "," : ""} ${zipCode || ""} ${
      city || ""
    }`.trim();
    return label;
  }, []);

  useEffect(() => {
    const { street, zipCode, city } = addressValues(values);
    if (showManualForm) {
      const isManualValid = street?.trim() && zipCode?.trim() && city?.trim();
      setIsAddressValid(!!isManualValid);
    } else {
      const isAutoValid =
        selectedAddressLabel?.trim() &&
        street?.trim() &&
        zipCode?.trim() &&
        city?.trim();
      setIsAddressValid(!!isAutoValid);
    }
  }, [values, selectedAddressLabel, showManualForm]);

  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <>
      <Layout>
        <Head
          description={TRANSLATION.DESCRIPTION}
          title={TRANSLATION.TITLE}
          onBack={onBack}
        />
        <div className="__LMF--content LMF-content lmf__form">
          {!showManualForm && (
            <Row gutter={[16, 16]} justify={"center"}>
              <Col xs={24} md={20} lg={18}>
                <AutoCompleteInput
                  allowClear={{ clearIcon: <MdClose size={12} /> }}
                  size="large"
                  options={autocompleteOptions}
                  onSearch={(value) => {
                    setSelectedAddressLabel(value);
                    if (value.length >= 3) {
                      searchLineCapency(value);
                    } else {
                      setAutocompleteOptions([]);
                    }
                  }}
                  onSelect={(value, option) => {
                    const label =
                      option.label ||
                      option.value ||
                      `${option.streetNumber || ""} ${option.street || ""}, ${
                        option.postalCode || ""
                      } ${option.city || ""}`.trim();
                    setSelectedAddressLabel(label);
                    setFieldValue(
                      `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`,
                      `${option.streetNumber} ${option.street}` || "",
                    );
                    setFieldValue(
                      `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      option.postalCode || "",
                    );
                    setFieldValue(
                      `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      option.city || "",
                    );
                    setTimeout(() => {
                      formProps.validateForm();
                    }, 0);
                  }}
                  onChange={(value) => {
                    setSelectedAddressLabel(value);
                    if (!value) {
                      setFieldValue(
                        `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`,
                        "",
                      );
                      setFieldValue(
                        `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                        "",
                      );
                      setFieldValue(
                        `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                        "",
                      );
                    }
                  }}
                  defaultValue={defaultValue}
                  placeholder={TRANSLATION.FORM.POSTAL_ADDRESS.PLACEHOLDER}
                  className={classNames("autocomplete__wrapper", {
                    valid:
                      get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`,
                      ) &&
                      get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      ) &&
                      get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      ),
                  })}
                  disabled={locked}
                  style={{ width: "100%" }}
                  suffixIcon={
                    get(
                      values,
                      `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`,
                    ) &&
                    get(
                      values,
                      `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                    ) &&
                    get(
                      values,
                      `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                    ) ? (
                      <img src={InputChecked} alt="valid" />
                    ) : null
                  }
                />
                <p
                  className="link--blue pt-24 d-flex justify--center align--center"
                  onClick={() => setShowManualForm(true)}
                >
                  {TRANSLATION.FORM.NOT_FOUND_LINK}
                </p>
              </Col>
            </Row>
          )}

          {showManualForm && (
            <>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Input
                    label={TRANSLATION.FORM.STREET.LABEL}
                    placeholder={TRANSLATION.FORM.STREET.PLACEHOLDER}
                    className={classNames({
                      valid: get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`,
                      ),
                    })}
                    {...bindWebTextFieldInputProps({
                      name: `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`,
                      values,
                      setFieldValue,
                      ...formProps,
                    })}
                    suffix={
                      get(
                        formProps.errors,
                        `telecoms.${CONTACT_POINT_SYSTEM.STREET}.value`,
                      ) ? (
                        <img src={InputError} alt="" />
                      ) : (
                        <img src={InputChecked} alt="" />
                      )
                    }
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Input
                    label={TRANSLATION.FORM.ADDRESS_LOCATION.LABEL}
                    placeholder={TRANSLATION.FORM.ADDRESS_LOCATION.PLACEHOLDER}
                    optional={TRANSLATION.FORM.OPTIONAL}
                    className={classNames({
                      valid: get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.ADDRESS_LOCATION}.value`,
                      ),
                    })}
                    {...bindWebTextFieldInputProps({
                      name: `telecoms.${CONTACT_POINT_SYSTEM.ADDRESS_LOCATION}.value`,
                      values,
                      setFieldValue,
                      ...formProps,
                    })}
                    suffix={
                      get(
                        formProps.errors,
                        `telecoms.${CONTACT_POINT_SYSTEM.ADDRESS_LOCATION}.value`,
                      ) ? (
                        <img src={InputError} alt="" />
                      ) : (
                        <img src={InputChecked} alt="" />
                      )
                    }
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Input
                    label={TRANSLATION.FORM.ZIP_CODE.LABEL}
                    placeholder={TRANSLATION.FORM.ZIP_CODE.PLACEHOLDER}
                    className={classNames({
                      valid: get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      ),
                    })}
                    {...bindInputProps({
                      name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      values,
                      setFieldValue,
                      ...formProps,
                    })}
                    onChange={({ target: { value } }) => {
                      setFieldValue(
                        `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                        value,
                      );
                    }}
                    suffix={
                      get(
                        formProps.errors,
                        `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      ) ? (
                        <img src={InputError} alt="" />
                      ) : (
                        <img src={InputChecked} alt="" />
                      )
                    }
                    disabled={true}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Select
                    label={TRANSLATION.FORM.CITY.LABEL}
                    placeholder={TRANSLATION.FORM.CITY.PLACEHOLDER}
                    showSearch
                    loading={citiesLoading}
                    options={cities}
                    className={classNames({
                      valid: get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      ),
                    })}
                    {...bindInputProps({
                      name: `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      values,
                      setFieldValue,
                      ...formProps,
                    })}
                    onChange={(value) => {
                      setFieldValue(
                        `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                        value,
                      );
                    }}
                    suffix={
                      get(
                        formProps.errors,
                        `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      ) ? (
                        <img src={InputError} alt="" />
                      ) : (
                        <img src={InputChecked} alt="" />
                      )
                    }
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Input
                    label={TRANSLATION.FORM.COMPLEMENT.LABEL}
                    placeholder={TRANSLATION.FORM.COMPLEMENT.PLACEHOLDER}
                    optional={TRANSLATION.FORM.OPTIONAL}
                    className={classNames({
                      valid: get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`,
                      ),
                    })}
                    {...bindWebTextFieldInputProps({
                      name: `telecoms.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`,
                      values,
                      setFieldValue,
                      ...formProps,
                    })}
                    suffix={
                      get(
                        formProps.errors,
                        `telecoms.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`,
                      ) ? (
                        <img src={InputError} alt="" />
                      ) : (
                        <img src={InputChecked} alt="" />
                      )
                    }
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Input
                    label={TRANSLATION.FORM.RESIDENCE.LABEL}
                    placeholder={TRANSLATION.FORM.RESIDENCE.PLACEHOLDER}
                    optional={TRANSLATION.FORM.OPTIONAL}
                    className={classNames({
                      valid: get(
                        values,
                        `telecoms.${CONTACT_POINT_SYSTEM.RESIDENCE}.value`,
                      ),
                    })}
                    {...bindWebTextFieldInputProps({
                      name: `telecoms.${CONTACT_POINT_SYSTEM.RESIDENCE}.value`,
                      values,
                      setFieldValue,
                      ...formProps,
                    })}
                    suffix={
                      get(
                        formProps.errors,
                        `telecoms.${CONTACT_POINT_SYSTEM.RESIDENCE}.value`,
                      ) ? (
                        <img src={InputError} alt="" />
                      ) : (
                        <img src={InputChecked} alt="" />
                      )
                    }
                  />
                </Col>
              </Row>

              <p
                className="link--blue pt-24 d-flex justify--center align--center"
                onClick={() => setShowManualForm(false)}
              >
                {TRANSLATION.FORM.FOUND_LINK}
              </p>
            </>
          )}
        </div>

        <ActionButtons
          disabled={!isEmpty(formProps.errors)}
          onNext={handleSubmit}
          buttonText={TRANSLATION.NEXT}
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
        />
        <p className="__LMF--hint custom">{TRANSLATION.HINT}</p>
        <p className="__LMF--link">{TRANSLATION.POLICY}</p>
      </Layout>
    </>
  );
};

export default View;
